/**
 * OnboardingGuard Component (V37)
 *
 * 사용자가 약관 동의 및 비밀번호 변경을 완료했는지 확인하고,
 * 필요한 경우 해당 페이지로 리다이렉트합니다.
 *
 * 플로우:
 * 1. 약관 동의 필요 → /auth/terms
 * 2. 비밀번호 변경 필요 → /auth/change-password
 * 3. 권한 없음 (employee + roleIds 없음) → NoAccessPage
 * 4. 모두 완료 → children 렌더링
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { NoAccessPage } from '../../pages/auth/NoAccessPage';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const location = useLocation();
  const {
    isLoading,
    isAuthenticated,
    needsTermsAcceptance,
    needsPasswordChange,
    hasNoAccess,
    error,
  } = useOnboardingStatus();

  // 로딩 중
  if (isLoading) {
    return (
      <div style={loadingContainerStyle}>
        <Loader
          size={40}
          style={{
            color: '#3b82f6',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ marginTop: '12px', color: '#6b7280' }}>로딩 중...</p>
      </div>
    );
  }

  // 인증되지 않은 경우
  if (!isAuthenticated) {
    // V82.7 Phase 2.6: /login 리다이렉트 진단 로그
    console.log('[OnboardingGuard redirect /login]', {
      reason: '!isAuthenticated',
      isLoading,
      from: location?.pathname,
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // V55.38: 인증은 됐지만 온보딩 상태 조회 실패 시 에러 UI 표시
  if (error) {
    return (
      <div style={loadingContainerStyle}>
        <AlertCircle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
        <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1f2937' }}>
          계정 정보를 불러오는 중입니다
        </h2>
        <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
          잠시 후 다시 시도해 주세요.
          <br />
          문제가 지속되면 관리자에게 문의하세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <RefreshCw size={16} />
          새로고침
        </button>
      </div>
    );
  }

  // 현재 경로가 온보딩 페이지인 경우 통과
  const isOnboardingPage =
    location.pathname === '/auth/terms' ||
    location.pathname === '/auth/change-password';

  if (isOnboardingPage) {
    return <>{children}</>;
  }

  // 약관 동의 필요
  if (needsTermsAcceptance) {
    return <Navigate to="/auth/terms" replace />;
  }

  // 비밀번호 변경 필요
  if (needsPasswordChange) {
    return <Navigate to="/auth/change-password" replace />;
  }

  // V37: 권한 없음 (employee이면서 roleIds가 비어있는 경우)
  if (hasNoAccess) {
    return <NoAccessPage />;
  }

  // 모든 온보딩 완료 + 권한 있음
  return <>{children}</>;
}

const loadingContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#f9fafb',
};
